import React, { useState, useEffect } from 'react';
import { getCustomerId } from '../utils/stripe';
import { getGenerationCount } from '../utils/firebaseSettings';

const SubscriptionModal = ({ isOpen, onClose, userInfo, subscriptionPlan, trialEnd, isOnTrial, cancelAtPeriodEnd, currentPeriodEnd, currentPeriodStart }) => {
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [generationCount, setGenerationCount] = useState(null);
  const [generationLimit, setGenerationLimit] = useState(null);
  const [loadingGenerations, setLoadingGenerations] = useState(true);

  // Load generation count when modal opens
  useEffect(() => {
    const loadGenerationData = async () => {
      if (isOpen && userInfo?.email) {
        setLoadingGenerations(true);
        try {
          const { count } = await getGenerationCount(userInfo.email, currentPeriodStart);
          setGenerationCount(count);

          // Set limit based on plan
          const limits = {
            free: 0,
            basic: 0,
            pro: 500,
          };
          setGenerationLimit(limits[subscriptionPlan?.toLowerCase()] || 0);
        } catch (error) {
          console.error('Error loading generation count:', error);
        } finally {
          setLoadingGenerations(false);
        }
      }
    };
    loadGenerationData();
  }, [isOpen, userInfo, currentPeriodStart, subscriptionPlan]);

  if (!isOpen) return null;

  const isProPlan = subscriptionPlan === 'pro';

  // Debug logging
  console.log('SubscriptionModal props:', {
    subscriptionPlan,
    isOnTrial,
    trialEnd,
    cancelAtPeriodEnd,
    currentPeriodEnd,
    currentPeriodStart
  });

  // Calculate days remaining in trial
  const daysRemainingInTrial = trialEnd ? Math.max(0, Math.ceil((trialEnd - Date.now()) / (1000 * 60 * 60 * 24))) : 0;

  // Calculate days until cancellation (use trial end if on trial, otherwise period end)
  const cancellationDate = isOnTrial && trialEnd ? trialEnd : currentPeriodEnd;
  const daysUntilCancellation = cancellationDate ? Math.max(0, Math.ceil((cancellationDate - Date.now()) / (1000 * 60 * 60 * 24))) : 0;

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      const customerId = getCustomerId(userInfo?.email);
      if (!customerId) {
        alert('Customer ID not found. Please contact support.');
        setCanceling(false);
        return;
      }

      // Get customer's active subscription
      const subsResponse = await fetch(`https://api.stripe.com/v1/subscriptions?customer=${customerId}&limit=1`, {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
        },
      });

      const subsData = await subsResponse.json();
      const activeSubscriptions = subsData.data.filter(sub =>
        sub.status === 'active' || sub.status === 'trialing'
      );

      if (activeSubscriptions.length === 0) {
        alert('No active subscription found.');
        setCanceling(false);
        return;
      }

      const subscriptionId = activeSubscriptions[0].id;

      // Cancel the subscription at period end (not immediately)
      const cancelResponse = await fetch(`https://api.stripe.com/v1/subscriptions/${subscriptionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_STRIPE_SECRET_KEY}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          'cancel_at_period_end': 'true',
        }),
      });

      if (cancelResponse.ok) {
        alert('✅ Subscription canceled successfully. You will retain access until the end of your billing period.');
        window.location.reload();
      } else {
        const error = await cancelResponse.json();
        alert(`Failed to cancel subscription: ${error.error?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('Failed to cancel subscription. Please try again or contact support.');
    }
    setCanceling(false);
    setShowCancelConfirm(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Manage Your Subscription</h2>
            <p className="text-sm text-gray-600 mt-1">Manage your billing and preferences</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Subscription Info Card */}
          <div className={`bg-gradient-to-r ${cancelAtPeriodEnd ? 'from-red-50 to-orange-50 border-red-200' : isProPlan ? 'from-blue-50 to-indigo-50 border-blue-200' : 'from-gray-50 to-slate-50 border-gray-300'} rounded-lg p-6 border mb-6`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${isProPlan ? 'bg-blue-600 text-white' : 'bg-gray-600 text-white'}`}>
                    {isProPlan ? 'PRO' : 'BASIC'}
                  </span>
                  <span className="text-sm text-gray-600">{isProPlan ? '$9.99/month' : '$2.99/month'}</span>
                  {isOnTrial && !cancelAtPeriodEnd && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      Trial: {daysRemainingInTrial} {daysRemainingInTrial === 1 ? 'day' : 'days'} left
                    </span>
                  )}
                  {cancelAtPeriodEnd && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      Cancels in {daysUntilCancellation} {daysUntilCancellation === 1 ? 'day' : 'days'}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700">
                  {isProPlan
                    ? 'AI-powered email support with custom knowledge base and response personalities'
                    : 'Access to email board and basic support features'}
                </p>
                {/* Next Payment Date - Show for all active subscriptions */}
                {currentPeriodEnd && !cancelAtPeriodEnd && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="text-sm font-semibold text-blue-900">Next Payment</p>
                        <p className="text-xs text-gray-700 mt-1">
                          Your next payment of <strong>${isProPlan ? '9.99' : '2.99'}</strong> will be charged on <strong>{new Date(currentPeriodEnd).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {cancelAtPeriodEnd && cancellationDate && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-semibold text-red-800 mb-1">⚠️ Subscription Scheduled for Cancellation</p>
                    <p className="text-xs text-gray-700">
                      Your subscription will cancel on <strong>{new Date(cancellationDate).toLocaleDateString()}</strong>. You'll retain full access until then.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* AI Generation Usage */}
          {isProPlan && (
            <div className="mb-6 bg-white rounded-lg border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">AI Generation Usage</h3>
                {loadingGenerations ? (
                  <div className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm text-gray-500">Loading...</span>
                  </div>
                ) : (
                  <span className="text-sm font-medium text-gray-600">
                    {generationCount} / {generationLimit} used
                  </span>
                )}
              </div>

              {!loadingGenerations && (
                <>
                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        generationCount >= generationLimit
                          ? 'bg-red-500'
                          : generationCount > generationLimit * 0.8
                          ? 'bg-yellow-500'
                          : 'bg-blue-600'
                      }`}
                      style={{ width: `${Math.min((generationCount / generationLimit) * 100, 100)}%` }}
                    ></div>
                  </div>

                  {/* Status message */}
                  <div className="flex items-start gap-2 mt-3">
                    {generationCount >= generationLimit ? (
                      <>
                        <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-red-700">
                          You've reached your AI generation limit for this billing period. Your limit will reset on {currentPeriodEnd ? new Date(currentPeriodEnd).toLocaleDateString() : 'your next billing date'}.
                        </p>
                      </>
                    ) : generationCount > generationLimit * 0.8 ? (
                      <>
                        <svg className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-yellow-800">
                          You're running low on AI generations. You have {generationLimit - generationCount} remaining this billing period.
                        </p>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm text-gray-600">
                          You have {generationLimit - generationCount} AI generations remaining this billing period.
                        </p>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Features List */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">{isProPlan ? 'Your Pro Features' : 'Your Basic Features'}</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-sm text-gray-700">Email board with drag-and-drop</span>
              </div>
              {isProPlan && (
                <>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">AI-powered email response generation</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">Custom AI knowledge base</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">5 AI response personalities</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm text-gray-700">Priority support</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => {
                const portalUrl = process.env.REACT_APP_STRIPE_CUSTOMER_PORTAL_URL;
                if (portalUrl) {
                  window.open(portalUrl, '_blank');
                } else {
                  alert('Customer portal not configured. Please contact support.');
                }
              }}
              className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Manage Subscription & Billing
            </button>

            {!cancelAtPeriodEnd && !showCancelConfirm && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="w-full px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium"
              >
                Cancel Subscription
              </button>
            )}

            {!cancelAtPeriodEnd && showCancelConfirm && (
              <div className="border border-red-300 rounded-lg p-4 bg-red-50">
                <p className="text-sm text-gray-800 mb-3">
                  Are you sure you want to cancel? You'll lose access to all features at the end of your billing period.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleCancelSubscription}
                    disabled={canceling}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {canceling ? 'Canceling...' : 'Yes, Cancel'}
                  </button>
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    disabled={canceling}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
                  >
                    Keep Subscription
                  </button>
                </div>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500 text-center mt-3">
            Update payment method or view invoices in the billing portal
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
